import { useEffect, type ReactNode } from "react"
import {
  Home,
  Target,
  Sparkles,
  Library,
  HelpCircle,
  Settings,
  LogOut,
  Shield,
  ShieldCheck,
  Bell,
  MessageSquare,
  ChevronsUpDown,
  type LucideIcon,
} from "lucide-react"
import { useNavigate, useLocation } from "react-router-dom"
import { cn } from "@/lib/utils"
import { BrandIcon } from "@/components/BrandIcon"
import { Wordmark } from "@/components/Wordmark"
import {
  Sidebar as SidebarRoot,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  useSidebar,
} from "@/components/ui/sidebar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useAuthStore } from "@/stores/auth-store"
import { useMyWorkStore } from "@/stores/my-work-store"
import { useFeedbackStore } from "@/stores/feedback-store"
import { getInitials, getAvatarColor } from "@/lib/avatar"

// ===================
// Navigation model
// ===================

export interface NavigationItem {
  icon: LucideIcon
  label: string
  path: string
  /** Paths (besides `path`) on which this item is the current page. */
  activePrefixes?: string[]
  exact?: boolean
}

export const navigationItems: NavigationItem[] = [
  // My work's page has no rail item of its own; it is reached from Home.
  { icon: Home, label: "Home", path: "/app", exact: true, activePrefixes: ["/app/my-work"] },
  { icon: Target, label: "Bids", path: "/app/bids" },
  { icon: Sparkles, label: "Assistant", path: "/app/assistant" },
  {
    icon: Library,
    label: "Knowledge",
    path: "/app/knowledge",
    // The pre-R4 paths redirect into Knowledge; cover them for the redirect's one render.
    activePrefixes: ["/app/library", "/app/templates", "/app/prompts"],
  },
]

export const bottomItems: NavigationItem[] = [
  { icon: HelpCircle, label: "Help", path: "/app/help" },
  { icon: Settings, label: "Settings", path: "/app/settings" },
]

/** Settings deep links used by the avatar menu. */
export const ACCOUNT_SETTINGS_PATH = "/app/settings?tab=account"
export const NOTIFICATION_SETTINGS_PATH = "/app/settings?tab=notifications"

function matchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`)
}

export function isNavItemActive(item: NavigationItem, pathname: string): boolean {
  const own = item.exact ? pathname === item.path : matchesPrefix(pathname, item.path)
  return own || (item.activePrefixes ?? []).some((prefix) => matchesPrefix(pathname, prefix))
}

// ===================
// Provider
// ===================

/** Wraps the app shell in the rail primitive's provider (mobile sheet state only). */
export function AppSidebarProvider({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) {
  return <SidebarProvider className={className}>{children}</SidebarProvider>
}

// ===================
// Nav item
// ===================

function NavItem({
  item,
  isActive,
  isMobile,
  count,
  onClick,
}: {
  item: NavigationItem
  isActive: boolean
  isMobile: boolean
  count?: number | undefined
  onClick: () => void
}) {
  const Icon = item.icon
  const layout = isMobile ? "row" : "rail"
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        layout={layout}
        isActive={isActive}
        onClick={onClick}
        aria-current={isActive ? "page" : undefined}
      >
        <Icon aria-hidden="true" />
        <span>{item.label}</span>
      </SidebarMenuButton>
      {count !== undefined && count > 0 && (
        <SidebarMenuBadge
          layout={layout}
          data-testid={`nav-count-${item.label}`}
          aria-label={`${count} waiting on you`}
        >
          {count > 99 ? "99+" : count}
        </SidebarMenuBadge>
      )}
    </SidebarMenuItem>
  )
}

function UserAvatar({
  initials,
  color,
  size = "sm",
}: {
  initials: string
  color: { bg: string; text: string }
  size?: "sm" | "md"
}) {
  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full",
        size === "sm" ? "h-7 w-7" : "h-8 w-8",
        color.bg
      )}
    >
      <span className={cn("font-medium", size === "sm" ? "text-[11px]" : "text-xs", color.text)}>
        {initials}
      </span>
    </div>
  )
}

// ===================
// Sidebar (app rail)
// ===================

export function Sidebar() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, isPlatformAdmin, signOut } = useAuthStore()
  const { isMobile, setOpenMobile } = useSidebar()

  // Home's count: assigned sections plus comment threads waiting on you
  // (Kapil, 2026-09-28). Both arrive in the one guarded /my-work load.
  const ensureMyWork = useMyWorkStore((s) => s.ensureMyWork)
  const myWorkCount = useMyWorkStore((s) => s.items.length + s.comments.length)

  useEffect(() => {
    void ensureMyWork()
  }, [ensureMyWork])

  const counts: Record<string, number> = {
    "/app": myWorkCount,
  }

  const fullName = user?.user_metadata?.["full_name"] as string | undefined
  const userInitials = getInitials(fullName ?? user?.email)
  const displayName = fullName ?? user?.email ?? "User"
  const avatarColor = getAvatarColor(user?.id ?? "default")
  // Second line of the user row; skipped when the name already is the email.
  const secondaryEmail = user?.email && user.email !== displayName ? user.email : undefined

  const closeSheet = () => {
    if (isMobile) setOpenMobile(false)
  }

  const go = (path: string) => {
    void navigate(path)
    closeSheet()
  }

  const handleSendFeedback = () => {
    closeSheet()
    useFeedbackStore.getState().open()
  }

  const handleSignOut = async () => {
    closeSheet()
    await signOut()
    void navigate("/login", { replace: true })
  }

  return (
    <SidebarRoot>
      {/* h-12 matches the top bar. The rail shows the app-icon badge; the
          mobile sheet has room for the wordmark. */}
      <SidebarHeader
        className={cn(
          "h-12 flex-row items-center gap-0 py-0",
          isMobile ? "justify-start px-6" : "justify-center px-0"
        )}
      >
        {isMobile ? (
          <Wordmark className="whitespace-nowrap text-xl" />
        ) : (
          <BrandIcon size={28} tone="filled" />
        )}
      </SidebarHeader>

      <SidebarContent className={cn("gap-0 pt-2", isMobile ? "px-2" : "px-1.5")}>
        <SidebarMenu data-nav-group="main">
          {navigationItems.map((item) => (
            <NavItem
              key={item.path}
              item={item}
              isActive={isNavItemActive(item, location.pathname)}
              isMobile={isMobile}
              count={counts[item.path]}
              onClick={() => go(item.path)}
            />
          ))}
        </SidebarMenu>
      </SidebarContent>

      <SidebarFooter className={cn("gap-1 pb-3 pt-1", isMobile ? "px-2" : "px-1.5")}>
        <SidebarMenu data-nav-group="footer">
          {bottomItems.map((item) => (
            <NavItem
              key={item.path}
              item={item}
              isActive={isNavItemActive(item, location.pathname)}
              isMobile={isMobile}
              onClick={() => go(item.path)}
            />
          ))}
        </SidebarMenu>

        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  layout={isMobile ? "row" : "rail"}
                  aria-label="Account menu"
                  title={isMobile ? undefined : displayName}
                  className={cn(
                    "data-[state=open]:bg-foreground/[0.07] data-[state=open]:text-foreground",
                    isMobile ? "h-10 px-2" : "py-1"
                  )}
                >
                  <UserAvatar initials={userInitials} color={avatarColor} />
                  {isMobile && (
                    <>
                      <div className="grid flex-1 text-left text-sm leading-tight">
                        <span className="truncate font-medium text-foreground">{displayName}</span>
                        {secondaryEmail && <span className="truncate text-xs">{secondaryEmail}</span>}
                      </div>
                      <ChevronsUpDown className="ml-auto" aria-hidden="true" />
                    </>
                  )}
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side={isMobile ? "top" : "right"}
                align="end"
                sideOffset={8}
                className="min-w-56 rounded-lg"
              >
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  <UserAvatar initials={userInitials} color={avatarColor} size="md" />
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">{displayName}</span>
                    {secondaryEmail && (
                      <span className="truncate text-xs text-muted-foreground">{secondaryEmail}</span>
                    )}
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => go(ACCOUNT_SETTINGS_PATH)}>
                  <ShieldCheck className="mr-2 h-4 w-4" />
                  Account &amp; security
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => go(NOTIFICATION_SETTINGS_PATH)}>
                  <Bell className="mr-2 h-4 w-4" />
                  Notification settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleSendFeedback}>
                  <MessageSquare className="mr-2 h-4 w-4" />
                  Send feedback
                </DropdownMenuItem>
                {isPlatformAdmin && (
                  <DropdownMenuItem onClick={() => go("/app/admin")}>
                    <Shield className="mr-2 h-4 w-4" />
                    Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => void handleSignOut()}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </SidebarRoot>
  )
}
